import { Box, Chip, CircularProgress, Grid, Link, Stack, Typography } from '@mui/material';
import { useGetOne, useRecordContext } from 'react-admin';
import { DetailRow, EmptyState, RecordSection } from '../components/record/RecordPage.tsx';
import { AddRenewalsFromProfile } from './AddRenewalsFromProfile.tsx';

type ProfileRecord = Record<string, unknown> & {
  owners?: { name?: string; percentage?: number }[];
  locations?: { addressLine?: string }[];
  entities?: {
    legalEntityName?: string;
    dba?: string;
    entityType?: string;
    fein?: string;
    yearEstablished?: number;
    ownershipDetails?: string;
  }[];
};

type FieldDefinition = {
  key: string;
  label: string;
  format?: 'currency' | 'percent' | 'date' | 'link';
};

const generalFields: FieldDefinition[] = [
  { key: 'legalEntityName', label: 'Legal entity name' },
  { key: 'dba', label: 'DBA' },
  { key: 'entityType', label: 'Entity type' },
  { key: 'fein', label: 'FEIN' },
  { key: 'yearEstablished', label: 'Year established' },
  { key: 'website', label: 'Website', format: 'link' },
  { key: 'primaryContactName', label: 'Primary contact' },
  { key: 'primaryContactTitle', label: 'Primary contact title' },
  { key: 'primaryContactEmail', label: 'Primary contact email' },
  { key: 'primaryContactPhone', label: 'Primary contact phone' },
  { key: 'businessOwnerName', label: 'Business owner' },
  { key: 'businessOwnerDob', label: 'Business owner DOB', format: 'date' },
  { key: 'businessOwnerAddress', label: 'Business owner address' },
  { key: 'mailingAddress', label: 'Mailing address' },
  { key: 'numberOfLocations', label: 'Number of locations' },
  { key: 'statesOfOperation', label: 'States of operation' }
];

const operationsFields: FieldDefinition[] = [
  { key: 'detailedDescription', label: 'Description of operations' },
  { key: 'naicsCode', label: 'NAICS code' },
  { key: 'sicCode', label: 'SIC code' },
  { key: 'annualRevenue', label: 'Annual revenue', format: 'currency' },
  { key: 'projectedRevenue', label: 'Projected revenue', format: 'currency' },
  { key: 'employeesFullTime', label: 'Full-time employees' },
  { key: 'employeesPartTime', label: 'Part-time employees' },
  { key: 'estimatedAnnualPayroll', label: 'Estimated payroll', format: 'currency' },
  { key: 'subcontractedRevenuePercent', label: 'Revenue subcontracted', format: 'percent' }
];

const propertyFields: FieldDefinition[] = [
  { key: 'propertyOwnerType', label: 'Building owner or tenant' },
  { key: 'constructionType', label: 'Construction type' },
  { key: 'yearBuilt', label: 'Year built' },
  { key: 'buildingSquareFootage', label: 'Building square footage' },
  { key: 'occupiedSquareFootage', label: 'Occupied square footage' },
  { key: 'roofTypeAge', label: 'Roof type and age' },
  { key: 'hvacUpdates', label: 'Electrical, plumbing, and HVAC updates' },
  {
    key: 'businessPersonalPropertyValue',
    label: 'Business personal property value',
    format: 'currency'
  },
  { key: 'buildingValue', label: 'Building value', format: 'currency' }
];

const booleanLabels: Record<string, string> = {
  independentContractorsUsed: 'Independent contractors used',
  workOutsideUs: 'Work outside the U.S.',
  manufacturesProducts: 'Manufactures or distributes products',
  professionalServicesProvided: 'Professional services provided',
  patientCareProvided: 'Patient care provided',
  priorClaims: 'Prior claims in the last five years',
  sprinklered: 'Sprinklered',
  fireAlarmCentralStation: 'Central-station fire alarm',
  burglarAlarmCentralStation: 'Central-station burglar alarm',
  businessIncomeExposure: 'Business income exposure'
};

const coverageLabels: Record<string, string> = {
  coverageGeneralLiability: 'General liability',
  coverageProperty: 'Property',
  coverageCommercialAuto: 'Commercial auto',
  coverageHiredNonOwnedAuto: 'Hired/non-owned auto',
  coverageWorkersComp: 'Workers compensation',
  coverageUmbrellaExcess: 'Umbrella/excess',
  coverageProfessionalLiability: 'Professional liability',
  coverageCyber: 'Cyber',
  coverageDO: 'D&O',
  coverageEPLI: 'EPLI',
  coverageCrime: 'Crime',
  coverageInlandMarine: 'Inland/ocean marine',
  coverageIntellectualProperty: 'Intellectual property',
  coverageCreditInsurance: 'Credit insurance',
  coverageFlood: 'Flood',
  coveragePollution: 'Pollution liability',
  coverageBuildersRisk: 'Builders risk',
  coverageForeignTravel: 'Foreign travel',
  coverageForeignLiability: 'Foreign liability',
  coverageVolunteerAccident: 'Volunteer accident',
  coverageBond: 'Bond',
  coverageLiquorLiability: 'Liquor liability'
};

const documentLabels: Record<string, string> = {
  docCurrentPolicies: 'Current policies',
  docShareholderAgreements: 'Shareholder agreements',
  docAutomobileSchedule: 'Automobile schedule',
  docFiveYearLossRuns: 'Five-year loss runs',
  docMasterServiceAgreements: 'Master service agreements',
  docDriversList: 'Drivers list',
  docCompletedApplications: 'Completed applications',
  docStatementsOfWork: 'Statements of work',
  docOrganizationalChart: 'Organizational chart',
  docIndependentContractorAgreements: 'Independent contractor agreements',
  docExecutiveSummary: 'Executive summary',
  docSubcontractorAgreements: 'Subcontractor agreements',
  docPitchDecks: 'Pitch decks',
  docVendorAgreements: 'Vendor agreements',
  docResumesCVs: 'Resumes/CVs',
  docStaffingAgreements: 'Staffing agreements',
  docCurrentFinancials: 'Current financials',
  docLeaseAgreements: 'Lease agreements',
  docProformaFinancials: 'Pro forma financials',
  docManufacturingAgreements: 'Manufacturing agreements',
  docCapTable: 'Cap table',
  docPropertyScheduleCope: 'Property schedule with COPE'
};

const hasValue = (value: unknown) => value !== null && value !== undefined && value !== '';

const formatValue = (value: unknown, format?: FieldDefinition['format']) => {
  if (!hasValue(value)) return null;
  if (format === 'currency' && typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  }
  if (format === 'percent') return `${value}%`;
  if (format === 'date') {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
  }
  if (format === 'link') {
    const href = String(value).startsWith('http') ? String(value) : `https://${value}`;
    return (
      <Link href={href} target="_blank" rel="noreferrer">
        {String(value)}
      </Link>
    );
  }
  return String(value);
};

const FieldGrid = ({ profile, fields }: { profile: ProfileRecord; fields: FieldDefinition[] }) => {
  const visible = fields.filter((field) => hasValue(profile[field.key]));
  if (!visible.length) return <EmptyState>No information recorded in this section.</EmptyState>;
  return (
    <Grid container spacing={1}>
      {visible.map((field) => (
        <Grid key={field.key} size={{ xs: 12, sm: 6 }}>
          <DetailRow label={field.label}>
            <Typography variant="body2" component="div">
              {formatValue(profile[field.key], field.format)}
            </Typography>
          </DetailRow>
        </Grid>
      ))}
    </Grid>
  );
};

const TrueValueChips = ({
  profile,
  labels
}: {
  profile: ProfileRecord;
  labels: Record<string, string>;
}) => {
  const selected = Object.entries(labels).filter(([key]) => profile[key] === true);
  if (!selected.length) return <EmptyState>None selected.</EmptyState>;
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
      {selected.map(([key, label]) => (
        <Chip key={key} label={label} size="small" variant="outlined" />
      ))}
    </Stack>
  );
};

export const OpportunityProfileReadOnly = () => {
  const opportunity = useRecordContext<{ id?: number }>();
  const { data, isLoading, error } = useGetOne(
    'opportunity-profiles',
    { id: opportunity?.id ?? 0 },
    { enabled: Boolean(opportunity?.id), retry: false }
  );

  if (isLoading)
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  if (error || !data)
    return (
      <RecordSection title="Opportunity profile">
        <EmptyState>No opportunity profile has been created yet.</EmptyState>
      </RecordSection>
    );

  const profile = data as ProfileRecord;
  return (
    <Stack spacing={2}>
      <RecordSection title="General information">
        <FieldGrid profile={profile} fields={generalFields} />
      </RecordSection>

      {profile.owners?.length || profile.locations?.length || profile.entities?.length ? (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <RecordSection title="Ownership">
              {profile.owners?.length ? (
                <Stack spacing={0.5}>
                  {profile.owners.map((owner, index) => (
                    <DetailRow
                      key={`${owner.name}-${index}`}
                      label={owner.name || `Owner ${index + 1}`}
                    >
                      <Typography variant="body2">{owner.percentage ?? 0}%</Typography>
                    </DetailRow>
                  ))}
                </Stack>
              ) : (
                <EmptyState>No owners recorded.</EmptyState>
              )}
            </RecordSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <RecordSection title="Locations">
              {profile.locations?.length ? (
                <Stack spacing={0.75}>
                  {profile.locations.map((location, index) => (
                    <Typography variant="body2" key={`${location.addressLine}-${index}`}>
                      {location.addressLine || `Location ${index + 1}`}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <EmptyState>No locations recorded.</EmptyState>
              )}
            </RecordSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <RecordSection title="Additional entities">
              {profile.entities?.length ? (
                <Stack spacing={1}>
                  {profile.entities.map((entity, index) => (
                    <Box key={`${entity.legalEntityName}-${index}`}>
                      <Typography variant="subtitle2">
                        {entity.legalEntityName || `Entity ${index + 1}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[entity.dba, entity.entityType, entity.yearEstablished]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                      {entity.ownershipDetails ? (
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {entity.ownershipDetails}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <EmptyState>No additional entities recorded.</EmptyState>
              )}
            </RecordSection>
          </Grid>
        </Grid>
      ) : null}

      <RecordSection title="Business operations">
        <FieldGrid profile={profile} fields={operationsFields} />
        <Box sx={{ mt: 1.5 }}>
          <TrueValueChips profile={profile} labels={booleanLabels} />
        </Box>
      </RecordSection>

      <RecordSection
        title="Lines of coverage requested"
        action={<AddRenewalsFromProfile profile={profile} opportunityId={opportunity?.id} />}
      >
        <TrueValueChips profile={profile} labels={coverageLabels} />
        <Box sx={{ mt: 1.5 }}>
          <FieldGrid
            profile={profile}
            fields={[
              { key: 'coverageOther', label: 'Other coverage' },
              { key: 'desiredLimits', label: 'Desired limits' },
              { key: 'reasonForMarketing', label: 'Reason for marketing' }
            ]}
          />
        </Box>
      </RecordSection>

      <RecordSection title="Property information">
        <FieldGrid profile={profile} fields={propertyFields} />
      </RecordSection>
      <RecordSection title="Documents to request">
        <TrueValueChips profile={profile} labels={documentLabels} />
      </RecordSection>
    </Stack>
  );
};
